// One formatting helper for every date/time this page shows (badge, modal, exports), so they
// can never disagree. Everything is US Eastern, per the page-wide ET decision.

export const ET_ZONE = 'America/New_York';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const etParts = new Intl.DateTimeFormat('en-US', {
  timeZone: ET_ZONE,
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

// An ISO timestamp with no zone designator would be parsed as the browser's local time. The
// API sends UTC, so a missing designator is treated as UTC rather than shifting the instant.
const toDate = (iso) => {
  if (iso instanceof Date) return iso;
  const s = String(iso);
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  return new Date(/^\d{4}-\d{2}-\d{2}T/.test(s) && !hasZone ? `${s}Z` : s);
};

/** "Oct 14, 2026, 09:42 AM ET" — "—" for empty/invalid input. */
export const formatEt = (iso) => {
  if (!iso) return '—';
  const d = toDate(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Built from parts: newer ICU versions put a narrow no-break space before AM/PM, which
  // would make the text differ between browsers.
  const p = Object.fromEntries(etParts.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.month} ${p.day}, ${p.year}, ${p.hour}:${p.minute} ${String(p.dayPeriod).toUpperCase()} ET`;
};

const parseYmd = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
};

/** ET calendar date "2026-09-15" -> "Sep 15, 2026". No time-zone maths: it is already an ET date. */
export const formatEtDate = (ymd) => {
  const p = parseYmd(ymd);
  return p ? `${MONTHS[p.m - 1]} ${p.d}, ${p.y}` : '—';
};

/** "2026-08-28" -> "Aug 28" (column sub-headers). */
export const formatEtDayMonth = (ymd) => {
  const p = parseYmd(ymd);
  return p ? `${MONTHS[p.m - 1]} ${p.d}` : '—';
};

/** "Sep 15, 2026 – Oct 14, 2026", or a single date when both ends are the same day. */
export const formatEtRange = (from, to) => {
  if (!parseYmd(from) || !parseYmd(to)) return '';
  return String(from).slice(0, 10) === String(to).slice(0, 10)
    ? formatEtDate(from)
    : `${formatEtDate(from)} – ${formatEtDate(to)}`;
};

/** "Aug 28 – Oct 14" for narrow headers. */
export const formatEtShortRange = (from, to) =>
  parseYmd(from) && parseYmd(to) ? `${formatEtDayMonth(from)} – ${formatEtDayMonth(to)}` : '';
